import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import payrollApi from 'api/payrollApi';
import { ReasonForm } from 'components/Forms';
import { notificationToast } from 'components/notificationToast';
import { Notification } from 'constants/notification';
import styled from 'styled-components';
import CustomButton from 'styles/buttonStyled';
import { Reason } from 'types';
import { getErrorMessage } from 'utils/getErrorMessage';

interface IAdjustmentSalaryConfirmProps {
  monthYear: string;
  fetchListStatusTabPayrollAPI: () => void;
}

const ADJUSTMENT_SALARY_TYPES = {
  ADJUST: 'adjust',
  APPROVED: 'approved',
};

const ConfirmedButton = styled.div`
  background-color: var(--cl_success100);
  color: var(--cl_success600);
  font-weight: bold;
  border-radius: 5px;
  text-align: center;
  display: inline;
  padding: 5px 10px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 4px;
`;

export function AdjustmentSalaryConfirm({
  monthYear,
  fetchListStatusTabPayrollAPI,
}: IAdjustmentSalaryConfirmProps) {
  const { t } = useTranslation(['timesheet', 'action', 'payroll']);
  const [isConfirm, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);

  const checkConfirmAdjustmentSalaryAPI = async () => {
    setIsLoading(true);
    const response = await payrollApi.checkConfirmAdjustmentSalary({
      monthYear: monthYear,
      type: ADJUSTMENT_SALARY_TYPES.ADJUST,
    });
    setIsLoading(false);
    setIsConfirmed(response.data?.isConfirm || false);
  };

  useEffect(() => {
    checkConfirmAdjustmentSalaryAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthYear]);

  const handleConfirmOk = async () => {
    try {
      setIsLoadingConfirm(true);
      const response = await payrollApi.confirmAdjustmentSalary({
        monthYear: monthYear,
        status: ADJUSTMENT_SALARY_TYPES.APPROVED,
      });
      setIsLoadingConfirm(false);

      notificationToast(Notification.Type.Success, response.message, Notification.Duration._3s);
      checkConfirmAdjustmentSalaryAPI();
      fetchListStatusTabPayrollAPI();
    } catch (error) {
      setIsLoadingConfirm(false);
      const message = getErrorMessage(error);
      notificationToast(Notification.Type.Error, message, Notification.Duration._3s);
    }
  };

  const handleConfirm = () => {
    Modal.confirm({
      title: (
        <div style={{ fontWeight: 'bold' }}>{t('payroll:confirm_adjustment_salary_title')}</div>
      ),
      okButtonProps: { loading: isLoadingConfirm },
      okText: t('action:ok'),
      cancelText: t('action:cancel'),
      onOk: handleConfirmOk,
    });
  };

  const handleReset = async (reason: string) => {
    try {
      const params = {
        monthYear: monthYear,
        timesheetType: 'adjust',
        reason,
      };

      setIsLoadingConfirm(true);
      const response = await payrollApi.resetConfirmPayroll(params);
      setIsLoadingConfirm(false);
      notificationToast(Notification.Type.Success, response.message, Notification.Duration._3s);
      checkConfirmAdjustmentSalaryAPI();
      fetchListStatusTabPayrollAPI();
    } catch (error) {
      setIsLoadingConfirm(false);
      const message = getErrorMessage(error);
      notificationToast(Notification.Type.Error, message, Notification.Duration._3s);
    }
  };

  const handleResetAdjustment = () => {
    let reason = '';
    Modal.confirm({
      title: <div style={{ fontWeight: 'bold' }}>{t('action:reset_reason')}</div>,
      content: (
        <>
          <ReasonForm
            onSubmit={(payload: Reason) => {
              if (!payload.reason) return;
              reason = payload.reason;
            }}
          />
          <p style={{ margin: 0, color: 'red', fontStyle: 'italic' }}>
            {t('payroll:payroll_deleted', { monthYear })}
          </p>
        </>
      ),
      okButtonProps: { loading: isLoadingConfirm },
      okText: t('action:ok'),
      cancelText: t('action:cancel'),
      onOk: () => handleReset(reason),
      onCancel: () => (reason = ''),
    });
  };

  return isConfirm ? (
    <>
      <CustomButton loading={false} onClick={handleResetAdjustment}>
        {t('action:reset')}
      </CustomButton>
      <ConfirmedButton>{t('Confirmed')}</ConfirmedButton>
    </>
  ) : (
    <CustomButton loading={false} disabled={isLoading} onClick={handleConfirm}>
      {t('Confirm')}
    </CustomButton>
  );
}
